import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import i18n from "@/lang/i18n";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { GetTyreBrandsResponse } from "@/central-atoms/everest-fleet-pwa/responses";
import { useTyreEntryCheck } from "@/hooks";

// Create the schema dynamically based on canViewTyreInfoCheck
const createFormSchema = (canViewTyreInfoCheck: boolean) =>
  z.object({
    left_front_tyre_brand: canViewTyreInfoCheck
      ? z.string({ required_error: "Required" })
      : z.string().optional(),
    left_front_tyre_number: canViewTyreInfoCheck
      ? z.string({ required_error: "Required" })
      : z.string().optional(),
    right_front_tyre_brand: canViewTyreInfoCheck
      ? z.string({ required_error: "Required" })
      : z.string().optional(),
    right_front_tyre_number: canViewTyreInfoCheck
      ? z.string({ required_error: "Required" })
      : z.string().optional(),
    left_rear_tyre_brand: canViewTyreInfoCheck
      ? z.string({ required_error: "Required" })
      : z.string().optional(),
    left_rear_tyre_number: canViewTyreInfoCheck
      ? z.string({ required_error: "Required" })
      : z.string().optional(),
    right_rear_tyre_brand: canViewTyreInfoCheck
      ? z.string({ required_error: "Required" })
      : z.string().optional(),
    right_rear_tyre_number: canViewTyreInfoCheck
      ? z.string({ required_error: "Required" })
      : z.string().optional(),
    stepney_tyre_brand: canViewTyreInfoCheck
      ? z.string({ required_error: "Required" })
      : z.string().optional(),
    stepney_tyre_number: canViewTyreInfoCheck
      ? z.string({ required_error: "Required" })
      : z.string().optional(),
  });
interface CheckInViaDriverFormProps {
  tyreBrands: GetTyreBrandsResponse;
  handleSubmit: (data: z.infer<ReturnType<typeof createFormSchema>>) => void;
}

const CheckInViaDriverForm: React.FC<CheckInViaDriverFormProps> = ({ tyreBrands, handleSubmit }) => {

  const canViewTyreInfoCheck = useTyreEntryCheck();

  const formSchema = createFormSchema(canViewTyreInfoCheck);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    handleSubmit(data);
  };

  const selectItems = tyreBrands.data.data.brands.map((tb) => {
    return <SelectItem key={tb.name} value={tb.name}>{tb.name}</SelectItem>;
  });


  return (
    <Form {...form} >
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full pt-[30px]">
        {
          canViewTyreInfoCheck &&
          <>
            <FormLabel className="font-semibold text-secondary">Front Left</FormLabel>
            <div className="flex py-[10px]">
              <FormField
                control={form.control}
                name="left_front_tyre_brand"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center">
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="border-primary text-primary mr-[14px]">
                            <SelectValue placeholder="Brand" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {selectItems}
                        </SelectContent>
                      </Select>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="left_front_tyre_number"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <div className="flex items-center">
                      <FormControl>
                        <Input
                          type="text"
                          className="bg-transparent border-primary text-secondary placeholder-secondary"
                          placeholder="#123456"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormLabel className="font-semibold text-secondary">Front Right</FormLabel>
            <div className="flex py-[10px]">
              <FormField
                control={form.control}
                name="right_front_tyre_brand"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center">
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="border-primary text-primary mr-[14px]">
                            <SelectValue placeholder="Brand" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {selectItems}
                        </SelectContent>
                      </Select>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="right_front_tyre_number"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <div className="flex items-center">
                      <FormControl>
                        <Input
                          type="text"
                          className="bg-transparent border-primary text-secondary placeholder-secondary"
                          placeholder="#123456"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormLabel className="font-semibold text-secondary">Rear Left</FormLabel>
            <div className="flex py-[10px]">
              <FormField
                control={form.control}
                name="left_rear_tyre_brand"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center">
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="border-primary text-primary mr-[14px]">
                            <SelectValue placeholder="Brand" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {selectItems}
                        </SelectContent>
                      </Select>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="left_rear_tyre_number"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <div className="flex items-center">
                      <FormControl>
                        <Input
                          type="text"
                          className="bg-transparent border-primary text-secondary placeholder-secondary"
                          placeholder="#123456"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormLabel className="font-semibold text-secondary">Rear Right</FormLabel>
            <div className="flex py-[10px]">
              <FormField
                control={form.control}
                name="right_rear_tyre_brand"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center">
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="border-primary text-primary mr-[14px]">
                            <SelectValue placeholder="Brand" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {selectItems}
                        </SelectContent>
                      </Select>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="right_rear_tyre_number"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <div className="flex items-center">
                      <FormControl>
                        <Input
                          type="text"
                          className="bg-transparent border-primary text-secondary placeholder-secondary"
                          placeholder="#123456"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormLabel className="font-semibold text-secondary">Spare</FormLabel>
            <div className="flex py-[10px]">
              <FormField
                control={form.control}
                name="stepney_tyre_brand"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center">
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="border-primary text-primary mr-[14px]">
                            <SelectValue placeholder="Brand" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {selectItems}
                        </SelectContent>
                      </Select>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="stepney_tyre_number"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <div className="flex items-center">
                      <FormControl>
                        <Input
                          type="text"
                          className="bg-transparent border-primary text-secondary placeholder-secondary"
                          placeholder="#123456"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </>
        }
        <Button className="py-[15px] mt-[20px] mb-[30px] h-auto rounded-[10px] shadow-[-4px_10px_24px_#0033504a]" variant="secondary" type="submit">
          {i18n.t('confirm')}
        </Button>
      </form>
    </Form>
  )
}

export default CheckInViaDriverForm;